import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Col, Form, Modal, Row } from 'react-bootstrap';
import { toast } from 'react-toastify';
import axios from 'axios';
import moment from 'moment';

const AddNotesModal = ({
    show,
    setShow,
    holdid,
    notes,
    onNotesUpdate,
    called_note,
    called_by_username,
    called_date
}) => {
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const isDarkMode =
        document.documentElement.getAttribute('data-bs-theme') === 'dark';

    useEffect(() => {
        if (!show) return;

        if (notes && notes.length > 0) {
            const sortedNotes = [...notes].sort(
                (a, b) => new Date(a.created) - new Date(b.created)
            );
            setMessages(
                sortedNotes.map(n => ({
                    sender: n.created_user || n.note_from_username,
                    text: n.note,
                    timestamp: n.created
                        ? moment(n.created).local().format('MM/DD/YY, hh:mm:ss A')
                        : '',
                    from: n.note_from
                }))
            );
        } else if (called_note) {
            setMessages([
                {
                    sender:
                        called_by_username || localStorage.getItem('username') || 'User',
                    text: called_note,
                    timestamp: called_date
                        ? moment(called_date).local().format('MM/DD/YY, hh:mm:ss A')
                        : '',
                    from: 9
                }
            ]);
        } else {
            setMessages([]);
        }

        setNewMessage('');
    }, [show, notes, called_note, called_by_username, called_date]);

    const handleClose = () => setShow(false);

    const handleSendMessage = async () => {
        const note = newMessage.trim();

        if (!note) {
            toast.info('Please enter a note.');
            return;
        }

        const now = new Date();
        const userName = localStorage.getItem('username') || 'User';
        const messageToSend = {
            sender: userName,
            text: note,
            timestamp: moment(now).format('MM/DD/YY, hh:mm:ss A'),
            from: 9
        };

        setMessages(prevMessages => [...prevMessages, messageToSend]);
        setNewMessage('');

        const tokenStr = localStorage.getItem('accessToken');
        const calledNotesUrl = `${process.env.REACT_APP_SERVER_URL}/drugs/update-hold-call-notes/`;
        const calledNotesData = {
            hold_id: holdid,
            is_called: 1,
            username: userName,
            called_note: note,
            called_date: moment(now).format('YYYY-MM-DD HH:mm:ss')
        };

        try {
            const { data } = await axios.post(calledNotesUrl, calledNotesData, {
                headers: { Authorization: `Bearer ${tokenStr}` }
            });

            if (data.status) {
                toast.success('Notes added successfully');
                if (onNotesUpdate) {
                    onNotesUpdate(holdid, data.data);
                }
            } else {
                toast.error('Error occurred while adding notes');
            }
        } catch (error) {
            toast.error('Error occurred while adding notes');
        }
    };

    const handleKeyDown = e => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSendMessage();
        }
    };

    return (
        <Modal show={show} onHide={handleClose} size="lg">
            <Modal.Header closeButton>
                <Modal.Title as="h5" id="copyLinkModalLabel">
                    Internal Notes
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="bg-body-tertiary p-4">
                <div
                    className="mb-3 notes-wrapper"
                    style={{
                        height: '300px',
                        overflowY: 'auto',
                        border: '1px solid #ccc',
                        padding: '10px',
                        borderRadius: '5px',
                        backgroundColor: isDarkMode ? '#2c2f33' : '#f9f9f9'
                    }}
                >
                    {messages.length === 0 ? (
                        <div className="text-center text-muted mt-5">
                            No internal notes yet.
                        </div>
                    ) : (
                        messages.map((msg, index) => (
                            <div
                                key={`${msg.timestamp}-${index}`}
                                style={{
                                    marginBottom: '10px',
                                    textAlign: 'right'
                                }}
                            >
                                <div style={{ marginBottom: '5px' }}>
                                    <strong style={{ color: isDarkMode ? '#9ecbff' : '#007bff' }}>
                                        {msg.sender}
                                    </strong>{' '}
                                    <span style={{ fontSize: '0.8em', color: '#888' }}>
                                        {msg.timestamp}
                                    </span>
                                </div>
                                <div
                                    style={{
                                        display: 'inline-block',
                                        backgroundColor: isDarkMode ? '#444' : '#e9ecef',
                                        padding: '10px',
                                        borderRadius: '10px',
                                        maxWidth: '70%',
                                        textAlign: 'left',
                                        color: isDarkMode ? '#f8f9fa' : '#212529',
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word'
                                    }}
                                >
                                    {msg.text}
                                </div>
                            </div>
                        ))
                    )}
                </div>
                <Row className="w-100 align-items-center g-2">
                    <Col xs={10}>
                        <Form.Control
                            type="text"
                            placeholder="Type your message..."
                            value={newMessage}
                            onChange={e => setNewMessage(e.target.value)}
                            onKeyDown={handleKeyDown}
                        />
                    </Col>
                    <Col xs={2}>
                        <Button
                            variant="primary"
                            className="rounded-circle d-flex align-items-center justify-content-center"
                            style={{ width: '40px', height: '40px' }}
                            onClick={handleSendMessage}
                        >
                            <FontAwesomeIcon
                                icon="paper-plane"
                                style={{ fontSize: '1.2rem', color: 'white' }}
                            />
                        </Button>
                    </Col>
                </Row>
            </Modal.Body>
        </Modal>
    );
};

AddNotesModal.propTypes = {
    show: PropTypes.bool.isRequired,
    setShow: PropTypes.func.isRequired,
    holdid: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    notes: PropTypes.array,
    onNotesUpdate: PropTypes.func,
    called_note: PropTypes.string,
    called_by_username: PropTypes.string,
    called_date: PropTypes.string
};

export default AddNotesModal;
